/**
 * Wishlist to Cart Service
 * Industry-grade service for moving products from wishlist to cart
 * NO COLOR LOGIC - Single color product system
 * NO REDIRECTS - Instant conversion
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Get session ID for guest cart
 */
function getSessionId() {
  let id = localStorage.getItem('cart_session_id');
  if (!id) {
    id = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem('cart_session_id', id);
  }
  return id;
}

/**
 * Build request headers
 */
function buildHeaders(token) {
  const headers = {
    'Content-Type': 'application/json',
    'x-session-id': getSessionId(),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Move product from wishlist to cart
 * Uses stored size, or size from size picker when provided
 * NO COLOR LOGIC
 * 
 * @param {string} productId - Product ID to move
 * @param {string} token - Auth token
 * @param {string} [size] - Optional size (from size picker); when omitted, backend uses stored wishlist size or "S"
 * @returns {Promise<Object>} Result with cart data and wishlist count
 */
export async function moveToCart(productId, token, size = undefined) {
  const productIdStr = String(productId || '').trim();
  
  if (!productIdStr) {
    throw new Error('Product ID is required');
  }

  if (!/^[0-9a-fA-F]{24}$/.test(productIdStr)) {
    throw new Error(`Invalid product ID format: ${productIdStr}`);
  }

  const body = { productId: productIdStr };
  if (size != null && String(size).trim() !== '') {
    body.size = String(size).trim();
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // Allow for backend cold start

  try {
    const response = await fetch(`${API_URL}/wishlist/move-to-cart`, {
      method: 'POST',
      headers: buildHeaders(token),
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (controller.signal.aborted) {
      throw new Error('Request timed out. Please try again.');
    }

    const data = await response.json();

    if (!response.ok) {
      // Handle specific error codes
      const errorCode = data.code || 'UNKNOWN_ERROR';
      const errorMessage = data.message || `Failed to move to cart (${response.status})`;

      switch (errorCode) {
        case 'NOT_IN_WISHLIST':
          throw new Error('Product is not in your wishlist');
        case 'PRODUCT_NOT_FOUND':
          throw new Error('Product not found or unavailable');
        case 'OUT_OF_STOCK':
          throw new Error('Product is out of stock');
        case 'VARIANT_OUT_OF_STOCK':
          throw new Error('Selected size is out of stock');
        case 'INVALID_SIZE':
          throw new Error(data.message || 'Selected size is not available');
        case 'INSUFFICIENT_STOCK':
          throw new Error(data.message || 'Insufficient stock available');
        case 'CART_FULL':
          throw new Error('Cart is full (maximum 20 items)');
        case 'INVALID_PRODUCT_ID':
          throw new Error('Invalid product ID');
        default:
          throw new Error(errorMessage);
      }
    }

    if (!data.success) {
      throw new Error(data.message || 'Failed to move to cart');
    }

    // Backend response structure from success() helper:
    // { success: true, message: "...", data: { items, subtotal, ..., wishlistCount, selectedSize } }
    // Extract cart data from data.data (the success helper wraps it)
    const responseData = data.data || data;
    
    // Extract cart fields (items, subtotal, etc.) and separate wishlistCount/selectedSize
    const { wishlistCount, selectedSize, ...cartFields } = responseData;
    
    return {
      success: true,
      cart: {
        items: cartFields.items || [],
        subtotal: cartFields.subtotal || 0,
        shippingFee: cartFields.shippingFee || 0,
        total: cartFields.total || 0,
        discountAmount: cartFields.discountAmount || 0,
        count: cartFields.count || 0,
      },
      wishlistCount: wishlistCount || 0,
      selectedSize: selectedSize || 'S',
      message: data.message || 'Product moved to cart successfully',
    };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError' || controller.signal.aborted) {
      throw new Error('Request timed out. Please check your connection and try again.');
    }

    // Re-throw if it's already a user-friendly error
    if (error.message && !error.message.includes('fetch')) {
      throw error;
    }

    // Network errors
    if (error.message?.includes('fetch') || error.message?.includes('network')) {
      throw new Error('Network error. Please check your connection and try again.');
    }

    // Unknown errors
    throw new Error(error.message || 'An unexpected error occurred. Please try again.');
  }
}

/**
 * Check if product can be moved to cart
 * NO COLOR LOGIC - Only checks size availability
 * 
 * @param {Object} product - Product object with variants
 * @returns {Object} Analysis result
 */
export function analyzeProductForMoveToCart(product) {
  if (!product) {
    return {
      canMove: false,
      reason: 'Product not found',
      action: 'error',
    };
  }

  // Check availability
  if (product.availability === 'out_of_stock' || product.availability === false) {
    return {
      canMove: false,
      reason: 'Product is out of stock',
      action: 'error',
    };
  }

  // Check variants - if no variants, still allow move (backend will handle default size)
  // This is more lenient - backend will use stored size or default to "S"
  if (!product.variants || product.variants.length === 0) {
    // Still allow move - backend will handle it
    return {
      canMove: true,
      action: 'move',
    };
  }

  // Filter in-stock variants (NO COLOR CHECK)
  const inStockVariants = product.variants.filter(v => (v.stock || 0) > 0);

  if (inStockVariants.length === 0) {
    return {
      canMove: false,
      reason: 'All sizes are out of stock',
      action: 'error',
    };
  }

  // Product has in-stock variants - can move to cart
  // Backend will use stored size or default to "S"
  return {
    canMove: true,
    action: 'move',
  };
}

/**
 * Get in-stock size variants for a product (for size picker).
 * @param {Object} product - Product with variants
 * @returns {Array<{ size: string, stock: number }>}
 */
export function getInStockSizes(product) {
  if (!product || !product.variants || product.variants.length === 0) {
    return [];
  }
  return product.variants
    .filter(v => (v.stock || 0) > 0)
    .map(v => ({ size: v.size || String(v.size), stock: v.stock || 0 }));
}

/**
 * Whether to show size picker before move to cart (multiple in-stock sizes).
 * When true, show bottom sheet to pick size instead of using stored/default size.
 * @param {Object} product - Product with variants
 * @returns {boolean}
 */
export function shouldShowSizePicker(product) {
  const inStock = getInStockSizes(product);
  return inStock.length > 1;
}
